import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import './PurchasingAdminPanel.css';
import { useAuth } from '../../../contexts/AuthContext';
import { API_CONFIG } from '../../../utils/apiConfig';

const PriceListView = () => {
  const [lists, setLists] = useState([]);
  const [error, setError] = useState('');
  const [editingIndex, setEditingIndex] = useState(null);
  const [editedList, setEditedList] = useState(null);
  const { currentUser, userProfile } = useAuth();
  const API_BASE_URL = API_CONFIG.BASE_URL;
  const navigate = useNavigate();

  // Get Firebase ID token for API calls
  const getAuthToken = useCallback(async () => {
    if (!currentUser) {
      throw new Error('No authenticated user');
    }
    return await currentUser.getIdToken(true);
  }, [currentUser]);

  useEffect(() => {
    const fetchPriceLists = async () => {
      if (!currentUser) return;
      
      // Check permissions - only Super Admin and Purchase Admin can access
      if (!userProfile) {
        setError('User profile not found. Please login again.');
        return;
      }

      const userRole = userProfile.role?.toLowerCase();
      const userDept = userProfile.department?.toLowerCase();
      
      if (userRole !== 'superadmin' && userRole !== 'super_admin' && 
          !(userRole === 'admin' && userDept === 'purchase')) {
        setError('Access denied. Only Super Admin and Purchase Admin can access Price Lists.');
        return;
      }
      
      try {
        const token = await getAuthToken();
        const response = await fetch(`${API_BASE_URL}/api/price-lists`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.message || 'Failed to fetch price lists');
        }

        setLists(data.data || []);
      } catch (err) {
        console.error('Fetch error:', err);
        if (err.message.includes('404') || err.message.includes('Not Found')) {
          setError('Price lists endpoint not available on server. Price list features may be limited.');
        } else {
          setError(err.message);
        }
      }
    };

    fetchPriceLists();
  }, [currentUser, userProfile, getAuthToken, API_BASE_URL]);

  const handleEdit = (index) => {
    setEditingIndex(index);
    setEditedList({ ...lists[index], products: lists[index].products.map(p => ({ ...p })) });
  };

  const handleChange = (productIndex, field, value) => {
    const updated = { ...editedList };
    updated.products[productIndex][field] = value;

    if (field === 'profitPerPc' || field === 'qty') {
      const profit = parseFloat(updated.products[productIndex].profitPerPc || 0);
      const qty = parseInt(updated.products[productIndex].qty || 0);
      updated.products[productIndex].totalProfit = (profit * qty).toFixed(2);
    }

    setEditedList(updated);
  };

  const handleSave = async (index) => {
    try {
      const token = await getAuthToken();
      // Try to get ID from editedList first, then from lists
      const priceListId = editedList?.id || lists[index].id;
      
      
      if (!priceListId) {
        throw new Error('Price list ID not found');
      }
      
      const response = await fetch(`${API_BASE_URL}/api/price-lists/${priceListId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(editedList),
      });

      const data = await response.json();

      if (!response.ok) throw new Error(data.message || 'Failed to save');

      const updatedLists = [...lists];
      updatedLists[index] = editedList;
      setLists(updatedLists);
      setEditingIndex(null);
      setEditedList(null);
      alert('Price List Updated Successfully!');
    } catch (err) {
      console.error('Save error:', err);
      setError(err.message || 'Something went wrong');
    }
  };

  const handleDelete = async (index) => {
    if (window.confirm('Are you sure you want to delete this price list?')) {
      try {
        const token = await getAuthToken();
        const priceListId = lists[index].id;
        
        
        if (!priceListId) {
          throw new Error('Price list ID not found');
        }
        
        const response = await fetch(`${API_BASE_URL}/api/price-lists/${priceListId}`, {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const data = await response.json();

        if (!response.ok) throw new Error(data.message || 'Failed to delete');

        setLists(lists.filter((_, i) => i !== index));
        alert('Price List Deleted Successfully!');
      } catch (err) {
        console.error('Delete error:', err);
        setError(err.message || 'Something went wrong');
      }
    }
  };

  return (
    <div className="price-list-view">
      <style>
        {`
          /* Responsive CSS for Price List View */
          .price-list-view {
            padding: 20px;
            max-width: 100%;
          }
          
          .price-list-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 20px;
            flex-wrap: wrap;
            gap: 15px;
          }
          
          .price-list-header h2 {
            margin: 0;
            color: #333;
          }
          
          .create-button {
            background-color: #007bff;
            color: white;
            border: none;
            padding: 10px 20px;
            border-radius: 5px;
            cursor: pointer;
            font-size: 14px;
            font-weight: bold;
            white-space: nowrap;
            width: auto;
            min-width: auto;
          }
          
          .price-list-block {
            background: #fff;
            border: 1px solid #ddd;
            border-radius: 8px;
            margin-bottom: 20px;
            overflow: hidden;
            box-shadow: 0 2px 4px rgba(0,0,0,0.1);
          }
          
          .price-list-block-header {
            background: #f8f9fa;
            padding: 15px;
            border-bottom: 1px solid #ddd;
            display: flex;
            justify-content: space-between;
            align-items: center;
            flex-wrap: wrap;
            gap: 10px;
          }
          
          .price-list-block-header h4 {
            margin: 0;
            color: #333;
            font-size: 16px;
          }
          
          .button-group {
            display: flex;
            gap: 10px;
            flex-wrap: wrap;
          }
          
          .button-group button {
            padding: 6px 12px;
            border: none;
            border-radius: 4px;
            cursor: pointer;
            font-size: 12px;
            font-weight: bold;
            white-space: nowrap;
            width: auto;
            min-width: auto;
          }
          
          .save-button {
            background-color: #28a745;
            color: white;
          }
          
          .cancel-button {
            background-color: #6c757d;
            color: white;
          }
          
          .edit-button {
            background-color: #007bff;
            color: white;
          }
          
          .delete-button {
            background-color: #dc3545;
            color: white;
          }
          
          .trash-button {
            background-color: #dc3545;
            color: white;
            padding: 4px 8px;
            border: none;
            border-radius: 3px;
            cursor: pointer;
            font-size: 12px;
            width: auto;
            min-width: auto;
          }
          
          .trash-button:hover {
            background-color: #c82333;
          }
          
          .responsive-table-container {
            overflow-x: auto;
          }
          
          .price-list-table {
            width: 100%;
            min-width: 800px;
            border-collapse: collapse;
            font-size: 14px;
          }
          
          .price-list-table th,
          .price-list-table td {
            border: 1px solid #ddd;
            padding: 8px;
            text-align: left;
          }
          
          .price-list-table th {
            background-color:#997a8d;
            font-weight: bold;
            font-size: 12px;
            white-space: nowrap;
          }
          
          .price-list-table input {
            width: 100%;
            border: 1px solid #ccc;
            padding: 4px;
            font-size: 12px;
            box-sizing: border-box;
          }
          
          .mobile-cards {
            display: none;
          }
          
          .mobile-price-card {
            background: #fff;
            border: 1px solid #ddd;
            border-radius: 8px;
            margin-bottom: 20px;
            overflow: hidden;
            box-shadow: 0 2px 4px rgba(0,0,0,0.1);
          }
          
          .mobile-price-header {
            background: #f8f9fa;
            padding: 15px;
            border-bottom: 1px solid #ddd;
          }
          
          .mobile-price-title {
            font-weight: bold;
            color: #333;
            margin-bottom: 10px;
            font-size: 16px;
          }
          
          .mobile-price-actions {
            display: flex;
            gap: 10px;
            flex-wrap: wrap;
          }
          
          .mobile-price-actions button {
            padding: 8px 12px;
            border: none;
            border-radius: 4px;
            cursor: pointer;
            font-size: 12px;
            font-weight: bold;
            flex: 1;
            width: auto;
            min-width: auto;
          }
          
          .mobile-product-card {
            background: #fff;
            border: 1px solid #eee;
            border-radius: 6px;
            padding: 12px;
            margin: 10px;
            box-shadow: 0 1px 3px rgba(0,0,0,0.1);
          }
          
          .mobile-product-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 10px;
            padding-bottom: 8px;
            border-bottom: 1px solid #eee;
          }
          
          .mobile-product-title {
            font-weight: bold;
            color: #333;
            font-size: 14px;
          }
          
          .mobile-product-row {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 8px;
            margin-bottom: 8px;
            font-size: 13px;
          }
          
          .mobile-product-label {
            font-weight: bold;
            color: #666;
          }
          
          .mobile-product-value {
            color: #333;
          }
          
          .mobile-product-input {
            width: 100%;
            border: 1px solid #ccc;
            padding: 4px;
            border-radius: 3px;
            font-size: 13px;
          }
          
          .mobile-product-readonly {
            padding: 4px;
            background-color: #f8f9fa;
            border-radius: 3px;
            color: #333;
          }
          
          /* Mobile Styles */
          @media (max-width: 768px) {
            .price-list-view {
              padding: 10px;
            }
            
            .price-list-header {
              flex-direction: column;
              align-items: flex-start;
            }
            
            .price-list-header h2 {
              font-size: 20px;
            }
            
            .create-button {
              width: 100%;
              padding: 12px;
              font-size: 16px;
            }
            
            .desktop-table {
              display: none !important;
            }
            
            .mobile-cards {
              display: block !important;
            }
            
            .price-list-block-header {
              flex-direction: column;
              align-items: flex-start;
            }
            
            .button-group {
              width: 100%;
            }
            
            .button-group button {
              width: auto;
              min-width: auto;
              flex: 1;
              padding: 10px;
              font-size: 14px;
            }
          }
          
          /* Tablet Styles */
          @media (max-width: 1024px) and (min-width: 769px) {
            .price-list-table {
              font-size: 12px;
            }
            
            .price-list-table th,
            .price-list-table td {
              padding: 6px;
            }
            
            .price-list-table input {
              font-size: 11px;
              padding: 3px;
            }
          }
          
          @media (min-width: 769px) {
            .desktop-table {
              display: block !important;
            }
            .mobile-cards {
              display: none !important;
            }
          }
        `}
      </style>
      
      <button 
        className="bck-button" 
        onClick={() => {
          // Navigate back based on user role
          if (userProfile?.role?.toLowerCase() === 'superadmin' || userProfile?.role?.toLowerCase() === 'super_admin') {
            navigate('/super-admin/');
          } else {
            navigate('/purchasing-admin');
          }
        }}
        style={{
          backgroundColor: '#997a8d',
          color: 'white',
          border: 'none',
          padding: '10px 20px',
          borderRadius: '5px',
          cursor: 'pointer',
          fontSize: '14px',
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}
      >
        ← Back to Admin Panel
      </button>
      
      <div className="price-list-header">
        <h2>SAVED PRICE LISTS</h2>
        <button 
          className="create-button" 
          onClick={() => {
            // Navigate based on user role
            if (userProfile?.role?.toLowerCase() === 'superadmin' || userProfile?.role?.toLowerCase() === 'super_admin') {
              navigate('/add-price-list');
            } else {
              navigate('/purchasing-admin/add-price-list');
            }
          }}
        >
          Create New Price List
        </button>
      </div>
      
      {error && <p className="error">{error}</p>}
      
      {lists.length === 0 ? (
        <p>No price lists found.</p>
      ) : (
        lists.map((list, idx) => (
          <div key={idx} className="price-list-block">
            {/* Desktop Table View */}
            <div className="desktop-table">
              <div className="price-list-block-header">
                <h4>Valid from: {new Date(list.validFrom).toLocaleDateString()}</h4>
                <div className="button-group">
                  {editingIndex === idx ? (
                    <>
                      <button className="save-button" onClick={() => handleSave(idx)}>Save</button>
                      <button className="cancel-button" onClick={() => setEditingIndex(null)}>Cancel</button>
                    </>
                  ) : (
                    <>
                      <button className="edit-button" onClick={() => handleEdit(idx)}>Edit</button>
                      <button className="delete-button" onClick={() => handleDelete(idx)}>Delete</button>
                    </>
                  )}
                </div>
              </div>
              <div className="responsive-table-container">
                <table className="price-list-table">
                  <thead>
                    <tr>
                      <th>NO</th>
                      <th>ITEM NAME</th>
                      <th>ITEM ID</th>
                      <th>MRP</th>
                      <th>DP /PCS</th>
                      <th>SP</th>
                      <th>QTY</th>
                      <th>PROFIT /PCS</th>
                      <th>TOTAL PROFIT</th>
                      <th>ACTION</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(editingIndex === idx ? editedList.products : list.products).map((p, i) => (
                      <tr key={i}>
                        <td>{i + 1}</td>
                        <td>
                          {editingIndex === idx ? (
                            <input
                              value={p.itemName}
                              onChange={(e) => handleChange(i, 'itemName', e.target.value)}
                            />
                          ) : (
                            p.itemName
                          )}
                        </td>
                        <td>
                          {editingIndex === idx ? (
                            <input
                              value={p.itemId}
                              onChange={(e) => handleChange(i, 'itemId', e.target.value)}
                            />
                          ) : (
                            p.itemId
                          )}
                        </td>
                        <td>
                          {editingIndex === idx ? (
                            <input
                              value={p.mrp}
                              onChange={(e) => handleChange(i, 'mrp', e.target.value)}
                            />
                          ) : (
                            p.mrp
                          )}
                        </td>
                        <td>
                          {editingIndex === idx ? (
                            <input
                              value={p.dp}
                              onChange={(e) => handleChange(i, 'dp', e.target.value)}
                            />
                          ) : (
                            p.dp
                          )}
                        </td>
                        <td>
                          {editingIndex === idx ? (
                            <input
                              value={p.sp}
                              onChange={(e) => handleChange(i, 'sp', e.target.value)}
                            />
                          ) : (
                            p.sp
                          )}
                        </td>
                        <td>
                          {editingIndex === idx ? (
                            <input
                              value={p.qty}
                              onChange={(e) => handleChange(i, 'qty', e.target.value)}
                            />
                          ) : (
                            p.qty
                          )}
                        </td>
                        <td>
                          {editingIndex === idx ? (
                            <input
                              value={p.profitPerPc}
                              onChange={(e) => handleChange(i, 'profitPerPc', e.target.value)}
                            />
                          ) : (
                            p.profitPerPc
                          )}
                        </td>
                        <td>{p.totalProfit}</td>
                        <td>
                          <button 
                            type="button" 
                            className="trash-button" 
                            onClick={() => {
                              if (editingIndex === idx) {
                                // If editing, remove from editedList
                                if (editedList.products.length > 1) {
                                  const updatedProducts = editedList.products.filter((_, productIndex) => productIndex !== i);
                                  setEditedList({ 
                                    ...editedList, 
                                    products: updatedProducts,
                                    id: editedList.id // Explicitly preserve the ID
                                  });
                                } else {
                                  alert('At least one product row is required');
                                }
                              } else {
                                // If not editing, start edit mode and remove the row
                                const currentList = { ...lists[idx], products: lists[idx].products.map(p => ({ ...p })) };
                                if (currentList.products.length > 1) {
                                  const updatedProducts = currentList.products.filter((_, productIndex) => productIndex !== i);
                                  setEditedList({ 
                                    ...currentList, 
                                    products: updatedProducts,
                                    id: currentList.id // Explicitly preserve the ID
                                  });
                                  setEditingIndex(idx);
                                } else {
                                  alert('At least one product row is required');
                                }
                              }
                            }}
                            title="Delete this row"
                          >
                            🗑️
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            
            {/* Mobile Card View */}
            <div className="mobile-cards">
              <div className="mobile-price-card">
                <div className="mobile-price-header">
                  <div className="mobile-price-title">
                    Valid from: {new Date(list.validFrom).toLocaleDateString()}
                  </div>
                  <div className="mobile-price-actions">
                    {editingIndex === idx ? (
                      <>
                        <button className="save-button" onClick={() => handleSave(idx)}>Save</button>
                        <button className="cancel-button" onClick={() => setEditingIndex(null)}>Cancel</button>
                      </>
                    ) : (
                      <>
                        <button className="edit-button" onClick={() => handleEdit(idx)}>Edit</button>
                        <button className="delete-button" onClick={() => handleDelete(idx)}>Delete</button>
                      </>
                    )}
                  </div>
                </div>
                
                {(editingIndex === idx ? editedList.products : list.products).map((p, i) => (
                  <div key={i} className="mobile-product-card">
                    <div className="mobile-product-header">
                      <div className="mobile-product-title">Item #{i + 1}</div>
                      <button 
                        type="button" 
                        className="trash-button" 
                        onClick={() => {
                          if (editingIndex === idx) {
                            // If editing, remove from editedList
                            if (editedList.products.length > 1) {
                              const updatedProducts = editedList.products.filter((_, productIndex) => productIndex !== i);
                              setEditedList({ 
                                ...editedList, 
                                products: updatedProducts,
                                id: editedList.id // Explicitly preserve the ID
                              });
                            } else {
                              alert('At least one product row is required');
                            }
                          } else {
                            // If not editing, start edit mode and remove the row
                            const currentList = { ...lists[idx], products: lists[idx].products.map(p => ({ ...p })) };
                            if (currentList.products.length > 1) {
                              const updatedProducts = currentList.products.filter((_, productIndex) => productIndex !== i);
                              setEditedList({ 
                                ...currentList, 
                                products: updatedProducts,
                                id: currentList.id // Explicitly preserve the ID
                              });
                              setEditingIndex(idx);
                            } else {
                              alert('At least one product row is required');
                            }
                          }
                        }}
                        title="Delete this item"
                      >
                        🗑️
                      </button>
                    </div>
                    
                    <div className="mobile-product-row">
                      <div className="mobile-product-label">Item Name:</div>
                      <div className="mobile-product-value">
                        {editingIndex === idx ? (
                          <input
                            className="mobile-product-input"
                            value={p.itemName}
                            onChange={(e) => handleChange(i, 'itemName', e.target.value)}
                          />
                        ) : (
                          <div className="mobile-product-readonly">{p.itemName}</div>
                        )}
                      </div>
                    </div>
                    
                    <div className="mobile-product-row">
                      <div className="mobile-product-label">Item ID:</div>
                      <div className="mobile-product-value">
                        {editingIndex === idx ? (
                          <input
                            className="mobile-product-input"
                            value={p.itemId}
                            onChange={(e) => handleChange(i, 'itemId', e.target.value)}
                          />
                        ) : (
                          <div className="mobile-product-readonly">{p.itemId}</div>
                        )}
                      </div>
                    </div>
                    
                    <div className="mobile-product-row">
                      <div className="mobile-product-label">MRP:</div>
                      <div className="mobile-product-value">
                        {editingIndex === idx ? (
                          <input
                            className="mobile-product-input"
                            value={p.mrp}
                            onChange={(e) => handleChange(i, 'mrp', e.target.value)}
                          />
                        ) : (
                          <div className="mobile-product-readonly">{p.mrp}</div>
                        )}
                      </div>
                    </div>
                    
                    <div className="mobile-product-row">
                      <div className="mobile-product-label">DP/PCS:</div>
                      <div className="mobile-product-value">
                        {editingIndex === idx ? (
                          <input
                            className="mobile-product-input"
                            value={p.dp}
                            onChange={(e) => handleChange(i, 'dp', e.target.value)}
                          />
                        ) : (
                          <div className="mobile-product-readonly">{p.dp}</div>
                        )}
                      </div>
                    </div>
                    
                    <div className="mobile-product-row">
                      <div className="mobile-product-label">SP:</div>
                      <div className="mobile-product-value">
                        {editingIndex === idx ? (
                          <input
                            className="mobile-product-input"
                            value={p.sp}
                            onChange={(e) => handleChange(i, 'sp', e.target.value)}
                          />
                        ) : (
                          <div className="mobile-product-readonly">{p.sp}</div>
                        )}
                      </div>
                    </div>
                    
                    <div className="mobile-product-row">
                      <div className="mobile-product-label">Quantity:</div>
                      <div className="mobile-product-value">
                        {editingIndex === idx ? (
                          <input
                            className="mobile-product-input"
                            value={p.qty}
                            onChange={(e) => handleChange(i, 'qty', e.target.value)}
                          />
                        ) : (
                          <div className="mobile-product-readonly">{p.qty}</div>
                        )}
                      </div>
                    </div>
                    
                    <div className="mobile-product-row">
                      <div className="mobile-product-label">Profit/PCS:</div>
                      <div className="mobile-product-value">
                        {editingIndex === idx ? (
                          <input
                            className="mobile-product-input"
                            value={p.profitPerPc}
                            onChange={(e) => handleChange(i, 'profitPerPc', e.target.value)}
                          />
                        ) : (
                          <div className="mobile-product-readonly">{p.profitPerPc}</div>
                        )}
                      </div>
                    </div>
                    
                    <div className="mobile-product-row">
                      <div className="mobile-product-label">Total Profit:</div>
                      <div className="mobile-product-value">
                        <div className="mobile-product-readonly">{p.totalProfit}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))
      )}
    </div>
  );
};

export default PriceListView;